import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Select,
  MenuItem,
  TextField,
  FormControl,
  InputLabel,
  Grid,
  Button,
  IconButton,
  CircularProgress
} from '@mui/material';
import { Add, Delete, Download } from '@mui/icons-material';
import * as XLSX from 'xlsx';

import { fetchRoeData, fetchCOF as fetchCOFApi } from './api';
import {
  cMap,
  computeData,
  computeRowValues as computeRowValuesCalc,
  computeBlendValues as computeBlendValuesCalc,
  calculateSumProductCapital,
  calculateSumProductWeightWAL,
  processRawData
} from './calculations';

// Initial rows with default rating, yield, weight, and WAL
const initialRows = [
  { rating: 'RML', yld: 6.5, weight: 20, wal: 5 },
  { rating: 'AA', yld: 6.25, weight: 20, wal: 5 },
  { rating: 'A', yld: 7.44, weight: 20, wal: 5 },
  { rating: 'BBB', yld: 6.5, weight: 20, wal: 5 },
  { rating: 'CM1', yld: 6.5, weight: 20, wal: 5 },
];

// Blend row as a separate state
// It uses sum of weights from above rows and user-editable yield.
const initialBlendRow = { rating: 'Blend', yld: 6.0, weight: 0, wal: 0 };


const REGION_TENORS = {
  'NY': ['3Y', '5Y', '7Y'],
  'NAT': ['3Y', '5Y', '7Y'],
  'NAT Procap': ['3Y', '5Y', '7Y'],
  'NY SCP': ['3Y', '5Y', '7Y'],
  'SCP': ['3Y', '4Y', '5Y', '7Y'],
  'WAVG': ['3Y', '5Y', '7Y', 'All']
}

export default function Calculator({ onCapitalChargesUpdate }) {
  // Editable constants (entered as percentages where applicable)
  const [tenor, setTenor] = useState('5Y'); // yrs
  const [customTenor, setCustomTenor] = useState('5Y');
  const [region, setRegion] = useState('NAT');
  const [COF, setCOF] = useState(0); // %
  const [PAMFees, setPAMFees] = useState(0.30);          // %
  const [costOfDebt, setCostOfDebt] = useState(7.00);    // %
  const [debtToCapital, setDebtToCapital] = useState(20.00); // %
  const [equityMultiplier, setEquityMultiplier] = useState(1.25);
  const [taxRate, setTaxRate] = useState(21.00);         // %
  const [rbcTarget, setRBCTarget] = useState(400.00);    // %
  const [product, setProduct] = useState('SPDA')
  const [dataLoaded, setDataLoaded] = useState(false);
  const [cofLoading, setCofLoading] = useState(false);
  const [ratingRows, setRatingRows] = useState(initialRows);
  const [blendRow, setBlendRow] = useState(initialBlendRow);
  const [availableRatings, setAvailableRatings] = useState([]);
  const [data, setData] = useState([]);
  // Raw data from backend (fetched once)
  const [roeData, setRoeData] = useState([]);
  const [capitalInputsData, setCapitalInputsData] = useState([]);
  const [covData, setCovData] = useState([]);
  // Capital Charges data frame (recalculated when region/product changes)
  const [capitalCharges, setCapitalCharges] = useState([]);
  const [alternativeCOFs, setAlternativeCOFs] = useState({
    'FHLB': { description: 'FHLB Additional Draw', liabilityType: 'Fixed', formula: '5y Tsy + 15', value: 0 },
    'GAS Prepay': { description: 'GAS Prepay', liabilityType: 'Fixed', formula: '5y Tsy + 145', value: 0 },
    'SFA': { description: 'NWL SFA', liabilityType: 'Floating', formula: '5y SOFR + 100', value: 0 }
  });

  const fetchData = async () => {
    try {
        const response = await fetchRoeData();

        // Store raw data from backend
        setRoeData(response.data.roe || []);
        setCapitalInputsData(response.data.capital_inputs || []);
        setCovData(response.data.cov || []);

        setDataLoaded(true);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const fetchCOF = async () => {
    setCofLoading(true);
    try {
      // For 'NAT Procap', use 'NAT' for COF lookup
      const cofRegion = region === 'NAT Procap' ? 'NAT' : region;

      const response = await fetchCOFApi({
        tenor: tenor,
        region: cofRegion,
        product: product
      });
      setCOF(response.data.COF);
      if (region == 'WAVG' && tenor == 'All'){
        setCustomTenor(response.data.TENOR);
      }
    } catch (error) {
      console.error('Error fetching COF:', error);
    } finally {
      setCofLoading(false);
    }
  };

  const fetchAlternativeCOFs = async () => {
    try {
      // Fetch alternative COFs for FHLB, GAS Prepay, and SFA
      const altProducts = ['FHLB', 'GAS Prepay', 'SFA'];
      const altCOFPromises = altProducts.map(altProduct =>
        fetchCOFApi({
          tenor: '5Y',
          region: 'NAT',
          product: altProduct
        })
      );

      const altCOFResponses = await Promise.all(altCOFPromises);

      setAlternativeCOFs(prev => {
        const updated = {...prev};
        altProducts.forEach((altProduct, index) => {
          if (updated[altProduct]) {
            updated[altProduct] = {
              ...updated[altProduct],
              value: altCOFResponses[index].data.COF
            };
          }
        });
        return updated;
      });
    } catch (error) {
      console.error('Error fetching alternative COFs:', error);
    }
  };

  useEffect(() => {
    if (product !== 'SPDA') {
      setTenor('5Y');
      setRegion('NAT');
      // Use the pre-calculated alternative COF value instead of fetching
      if (alternativeCOFs[product] && alternativeCOFs[product].value) {
        setCOF(alternativeCOFs[product].value);
      }
      setCofLoading(false); // No need to fetch COF for non-SPDA products
    } else {
      fetchCOF();
    }
  }, [tenor, region, product]);

  // Fetch data once on component mount
  useEffect(() => {
    fetchData();
    fetchAlternativeCOFs();
  }, []);

  // Process raw data whenever product or region changes
  useEffect(() => {
    if (dataLoaded && roeData.length > 0) {
      const processedData = processRawData(roeData, capitalInputsData, covData, product, region);
      
      const filteredData = processedData.filter(row => row.RATING);
      setData(filteredData);
      
      // Create Capital Charges data frame with all calculated fields
      const capitalChargesData = filteredData.map(row => {
        // Format ASOFDT as YYYY-MM-DD
        let formattedDate = row.ASOFDT;
        if (row.ASOFDT) {
          const date = new Date(row.ASOFDT);
          if (!isNaN(date.getTime())) {
            formattedDate = date.toISOString().split('T')[0];
          }
        }
        
        return {
          RATING: row.RATING,
          BASE_RBC: row.BASE_RBC,
          TAX: row.TAX,
          COV: row.COV,
          SIZE_FACTOR: row.SIZE_FACTOR,
          PRE_TARGET: row.PRE_TARGET,
          TARGET: row.TARGET,
          EFF_RBC: row.EFF_RBC,
          C1: row.C1,
          ASOFDT: formattedDate
        };
      });
      setCapitalCharges(capitalChargesData);
      
      // Make capitalCharges available globally for popup or debugging
      window.capitalCharges = capitalChargesData;
      
      // Send to parent component (App.js) for Capital Charges popup
      if (onCapitalChargesUpdate) {
        const metadata = { product, region, tenor };
        onCapitalChargesUpdate(capitalChargesData, metadata);
      }
      
      const uniqueRatings = [...new Set(filteredData.map(row => row.RATING))];
      setAvailableRatings(uniqueRatings);
    }
  }, [dataLoaded, roeData, capitalInputsData, covData, product, region, tenor, onCapitalChargesUpdate]);

  const getAvailableTenors = (selectedRegion) => {
    return REGION_TENORS[selectedRegion] || [];
  };

  useEffect(() => {
    const availableTenors = getAvailableTenors(region);
    if (!availableTenors.includes(tenor)) {
      setTenor(availableTenors[0]); // Set to first available tenor if current is invalid
    }
  }, [region]);


  // Recompute WT_REQ whenever tenor or data changes
  const computedData = useMemo(() => {
    if (!dataLoaded || data.length === 0) {
      return {};
    }
    const result = computeData(data, product, tenor, region, customTenor);
    return result;
  }, [dataLoaded, data, tenor, product, region, customTenor]);

  const computeRowValues = (rating, yld) => {
    if (!dataLoaded) {
      return {
        netYield: 'N/A',
        capital: 'N/A',
        capitalDecimal: null,
        naicLeverage: 'N/A',
        unleveredROE: 'N/A',
        leveredROE: 'N/A'
      };
    }
    const result = computeRowValuesCalc(
      rating,
      yld,
      computedData,
      COF,
      PAMFees,
      costOfDebt,
      debtToCapital,
      equityMultiplier,
      taxRate
    );
    return result;
  };

  const handleRatingChange = (index, newRating) => {
    const updatedRows = [...ratingRows];
    updatedRows[index].rating = newRating;
    setRatingRows(updatedRows);
  };

  const handleYieldChange = (index, newYield) => {
    const updatedRows = [...ratingRows];
    updatedRows[index].yld = parseFloat(newYield) || 0;
    setRatingRows(updatedRows);
  };

  const handleWeightChange = (index, newWeight) => {
    const updatedRows = [...ratingRows];
    updatedRows[index].weight = parseFloat(newWeight) || 0;
    setRatingRows(updatedRows);
  };

  const handleWALChange = (index, newWAL) => {
    const updatedRows = [...ratingRows];
    updatedRows[index].wal = parseFloat(newWAL) || 0;
    setRatingRows(updatedRows);
  };

  const recalculateWeights = (rows) => {
    const numRows = rows.length;
    const updatedRows = rows.map(row => ({
      ...row,
      weight: (100 / numRows).toFixed(2),
    }));
  
  
    setRatingRows(updatedRows);
  };
  
  const handleAddRow = () => {
    const updatedRows = [...ratingRows, { rating: '', yld: 0, weight: 0, wal: 0 }];
    recalculateWeights(updatedRows);
  };
  
  const handleDeleteRow = (index) => {
    const updatedRows = ratingRows.filter((_, idx) => idx !== index);
    recalculateWeights(updatedRows);
  };
  
  const normalRowData = ratingRows.map((row, idx) => {
    const values = computeRowValues(row.rating, row.yld);
    return { ...row, ...values };
  });

  // sumProductCapital from normal rows
  const sumProductCapital = calculateSumProductCapital(normalRowData);

  // Sum of product of weights and WALs for the blend row's weight
  const sumProductWeightWAL = calculateSumProductWeightWAL(normalRowData);

  // Compute Blend row values using sumProductCapital
  const blendValues = computeBlendValuesCalc(
    normalRowData,
    sumProductCapital,
    sumProductWeightWAL,
    COF,
    PAMFees,
    costOfDebt,
    debtToCapital,
    equityMultiplier,
    taxRate
  );

  // Update blend row weight to sumWeights, not editable
  const blendWeight = normalRowData.reduce((acc, r) => acc + (parseFloat(r.weight) || 0), 0).toFixed(2);
 // sum of weights

  const roeCellStyle = {
    backgroundColor: '#D3D3D3', 
    color: '#fff'
  };

  const handleDownload = () => {
    const formatRowValues = (row) => {
      const { capitalDecimal, ...rest } = row; // Destructure to remove capitalDecimal
      return {
        ...rest,
        netYield: row.netYield ? (parseFloat(row.netYield.replace('%', '')) / 100).toString() : '0',
        capital: row.capital ? (parseFloat(row.capital.replace('%', '')) / 100).toString() : '0',
        naicLeverage: row.naicLeverage ? row.naicLeverage.replace('x', '') : '0',
        unleveredROE: row.unleveredROE ? (parseFloat(row.unleveredROE.replace('%', '')) / 100).toString() : '0',
        leveredROE: row.leveredROE ? (parseFloat(row.leveredROE.replace('%', '')) / 100).toString() : '0'
      };
    };

    // Format all rows including blend row
    const formattedRows = normalRowData.map(formatRowValues);
    const formattedBlendRow = {
      ...blendRow,
      netYield: (blendValues.netYield.replace('%', '') / 100).toString(),
      capital: (blendValues.capital.replace('%', '') / 100).toString(),
      naicLeverage: blendValues.naicLeverage.replace('x', ''),
      unleveredROE: (blendValues.unleveredROE.replace('%', '') / 100).toString(),
      leveredROE: (blendValues.leveredROE.replace('%', '') / 100).toString(),
      weight: blendWeight.toString()
    };

    // Create a sheet for all values
    const wsSummary = XLSX.utils.json_to_sheet(formattedRows.concat([formattedBlendRow]));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, wsSummary, 'Summary');
    XLSX.writeFile(wb, 'summary.xlsx');
  };

  const handleCOFChange = (e) => {
    const value = e.target.value === '' ? '' : Number(e.target.value);
    if (!isNaN(value)) {
      setCOF(value);
    }
  };

  return (
    <Box p={2}>
      {/* Constants Editing Section */}
      <Typography variant="h6" gutterBottom>Constants</Typography>
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth>
            <InputLabel>Region</InputLabel>
            <Select
              value={region}
              label="Region"
              disabled={product != 'SPDA' || cofLoading}
              onChange={(e) => setRegion(e.target.value)}
            >
              {Object.keys(REGION_TENORS).map(r => (
                <MenuItem key={r} value={r}>{r}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth>
            <InputLabel>Tenor (yrs)</InputLabel>
            <Select
              value={tenor}
              label="Tenor"
              disabled={product != 'SPDA' || cofLoading}
              onChange={(e) => setTenor(e.target.value)}
            >
               {getAvailableTenors(region).map(t => (
                <MenuItem key={t} value={t}>{t}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth>
            <InputLabel>Product</InputLabel>
            <Select
              value={product}
              label="Product"
              disabled={cofLoading}
              onChange={(e) => setProduct(e.target.value)}
            >
               {Object.keys(cMap).map(t => (
                <MenuItem key={t} value={t}>{t}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label={cofLoading ? "Cost of Funds (COF) (%) - Loading..." : "Cost of Funds (COF) (%)"}
            type="number"
            value={COF === '' ? '' : Number(COF).toFixed(2)}
            onChange={handleCOFChange}
            fullWidth
            disabled={cofLoading}
            inputProps={{step: "0.01"}}
          />
        </Grid>
      </Grid>
      {dataLoaded ? (
        <Box sx={{ position: 'relative' }}>
          {cofLoading && (
            <Box 
              sx={{ 
                position: 'absolute', 
                top: 0, 
                left: 0, 
                right: 0, 
                bottom: 0, 
                backgroundColor: 'rgba(255, 255, 255, 0.8)', 
                zIndex: 10,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2
              }}
            >
              <CircularProgress size={50} />
              <Typography variant="h6" color="textSecondary">Updating COF...</Typography>
            </Box>
          )}
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" gutterBottom>Summary</Typography>
            <Button variant="contained" onClick={handleDownload} startIcon={<Download />} sx={{ backgroundColor: 'lightgrey', color: 'black' }}>
              Download
            </Button>
          </Box>
          <Box sx={{pb:2}}></Box>
          <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
            <TableHead>
              <TableRow sx={roeCellStyle}>
                <TableCell>Rating</TableCell>
                <TableCell>Yield (%)</TableCell>
                <TableCell>Weight (%)</TableCell>
                <TableCell>WAL</TableCell>
                <TableCell>Net Yield</TableCell>
                <TableCell>Capital</TableCell>
                <TableCell>NAIC Leverage</TableCell>
                <TableCell>Unlevered ROE</TableCell>
                <TableCell>Levered ROE</TableCell>
                <TableCell>Delete</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {normalRowData.map((r, idx) => (
                <TableRow key={idx}>
                  <TableCell>
                    <FormControl fullWidth>
                      <InputLabel>Rating</InputLabel>
                      <Select
                        value={r.rating}
                        label="Rating"
                        onChange={(e) => handleRatingChange(idx, e.target.value)}
                      >
                        {availableRatings.map(rate => (
                          <MenuItem key={rate} value={rate}>{rate}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell>
                    <TextField
                      type="number"
                      value={r.yld}
                      onChange={(e) => handleYieldChange(idx, e.target.value)}
                      fullWidth
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      type="number"
                      value={r.weight}
                      onChange={(e) => handleWeightChange(idx, e.target.value)}
                      fullWidth
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      type="number"
                      value={r.wal}
                      onChange={(e) => handleWALChange(idx, e.target.value)}
                      fullWidth
                    />
                  </TableCell>
                  <TableCell>{r.netYield}</TableCell>
                  <TableCell>{r.capital}</TableCell>
                  <TableCell>{r.naicLeverage}</TableCell>
                  <TableCell>{r.unleveredROE}</TableCell>
                  <TableCell>{r.leveredROE}</TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleDeleteRow(idx)}>
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}

              {/* Blend Row - weight = sum of product of weights and WALs above, not editable */}
              <TableRow>
                <TableCell><strong>{blendRow.rating}</strong></TableCell>
                <TableCell align="center">{blendValues.yld.replace('%', '')}%</TableCell>
                <TableCell align="center">{blendWeight}%</TableCell>
                <TableCell></TableCell>
                <TableCell>{blendValues.netYield}</TableCell>
                <TableCell>{blendValues.capital}</TableCell>
                <TableCell>{blendValues.naicLeverage}</TableCell>
                <TableCell>{blendValues.unleveredROE}</TableCell>
                <TableCell>{blendValues.leveredROE}</TableCell>
                <TableCell></TableCell>
              </TableRow>

            </TableBody>
          </Table>
          <Button onClick={handleAddRow} startIcon={<Add />} sx={{ backgroundColor: 'lightgrey', color: 'black', mt: 2}}>
            Add Row
          </Button>
          <Box mt={2} mb={2}>
            <Typography variant="h6">Assumptions:</Typography>
            <Typography variant="body2">
              <ul>
                <li>Multiple tranches blend will be the weighted average by WAL</li>
                <li>Equity Multiplier: {equityMultiplier}</li>
                <li>Tax Rate: {taxRate}%</li>
                <li>4Q2024 SUSA capital factors: {rbcTarget}% RBC, assumes Procap relief for NAT and non NY SCP</li>
                <li>Management Fee: {PAMFees * 100} bps</li>
                <li>Debt to Capital: {debtToCapital}%</li>
                <li>Cost of Debt: {costOfDebt}%</li>
              </ul>
            </Typography>
            
            <Typography variant="h6" mt={2}>Alternative Cost of Funds Assumptions ({new Date().toLocaleDateString('en-US', {month: '2-digit', day: '2-digit', year: 'numeric'}).replace(/\//g, '-')}):</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Product</strong></TableCell>
                  <TableCell><strong>Liability Type</strong></TableCell>
                  <TableCell><strong>Cost of Funds</strong></TableCell>
                  <TableCell><strong>Spot Cost of Funds</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(alternativeCOFs).map(([key, cofData]) => (
                  <TableRow key={key}>
                    <TableCell>{cofData.description}</TableCell>
                    <TableCell>{cofData.liabilityType}</TableCell>
                    <TableCell>{cofData.formula}</TableCell>
                    <TableCell>{cofData.value ? `${cofData.value.toFixed(2)}%` : ''}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '300px', gap: 2 }}>
          <CircularProgress size={60} />
          <Typography variant="h6" color="textSecondary">Loading data...</Typography>
        </Box>
      )}
    </Box>
  );
}
